import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../../../component/ui/Button';
import DataNotFound from '../../components/dataNotFound';
import CreateNewListModal from './CreateNewListModal';
import Table from '../../../component/table/Table';
import Pagination from '../../../component/pagination';
import { getListTableColumns } from './components/ListTableColumn';
import sortbydot from '../../../assets/image/sort_by_group.svg';
import { toast } from 'react-toastify';
import { getProductLists, deleteProductList } from '../../../store/action/inventory';
import './List.scss';

const ProductLists = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [headerDropDown, setHeaderDropDown] = useState('All Lists');
	const [row, setRow] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
	const [selectedRows, setSelectedRows] = useState([]);
	const [sortDirection, setSortDirection] = useState({ 'LIST NAME': 'asc' });
	const [totalPages, setTotalPages] = useState(1);

	const [params, setParams] = useState(() => ({
		page: Number(searchParams.get('page')) || 1,
		pageSize: Number(searchParams.get('pageSize')) || 20,
	}));

	const fetchProductListsData = useCallback(async () => {
		setLoading(true);
		try {
			const queryParam = `page=${params.page}&pageSize=${params.pageSize}`;
			const res = await getProductLists(queryParam);
			console.log('getProductLists API Response:', res);

			const listArray =
				res?.data?.product_lists ||
				res?.product_lists ||
				res?.data?.data?.product_lists ||
				(Array.isArray(res?.data) ? res.data : null) ||
				(Array.isArray(res) ? res : null);

			if (listArray && listArray.length > 0) {
				const mappedRows = listArray.map((item) => ({
					id: item.product_list_id || item.id,
					'LIST NAME': item.name || item['LIST NAME'],
					DESCRIPTION: item.description || '-',
					'CREATED DATE': item.created_at
						? new Date(item.created_at).toLocaleDateString('en-GB')
						: item['CREATED DATE'] || '-',
					'PRODUCT COUNT': item.products_count ?? item['PRODUCT COUNT'] ?? 0,
				}));
				setRow(mappedRows);
				setTotalPages(res?.data?.totalPages || res?.totalPages || 1);
			} else {
				setRow([]);
				setTotalPages(1);
			}
		} catch (err) {
			console.error('Failed to fetch product lists:', err);
			toast.error('Failed to fetch product lists');
			setRow([]);
		} finally {
			setLoading(false);
		}
	}, [params.page, params.pageSize]);

	useEffect(() => {
		fetchProductListsData();
	}, [fetchProductListsData]);

	const handleRefresh = () => {
		fetchProductListsData();
	};

	const handleSaveSuccess = () => {
		fetchProductListsData();
	};

	const handleDeleteSelected = async () => {
		if (!selectedRows || !selectedRows.length) return;
		try {
			setLoading(true);
			const targetId = selectedRows.length === 1 ? selectedRows[0] : selectedRows;
			const res = await deleteProductList(targetId);
			if (res?.success) {
				toast.success(res?.message || 'List(s) deleted successfully');
			} else {
				toast.success('List(s) deleted successfully');
			}
			setSelectedRows([]);
			fetchProductListsData();
		} catch (err) {
			console.error('Delete API Error:', err);
			toast.error('Failed to delete list(s)');
		} finally {
			setLoading(false);
		}
	};

	const handleSort = (fieldKey) => {
		const currentSort = sortDirection[fieldKey] === 'asc' ? 'desc' : 'asc';
		setSortDirection({ [fieldKey]: currentSort });
		setRow((prevRows) =>
			[...prevRows].sort((a, b) => {
				let valA = a[fieldKey] ?? '';
				let valB = b[fieldKey] ?? '';
				if (typeof valA === 'number' && typeof valB === 'number') {
					return currentSort === 'asc' ? valA - valB : valB - valA;
				}
				valA = String(valA).toLowerCase();
				valB = String(valB).toLowerCase();
				if (valA < valB) return currentSort === 'asc' ? -1 : 1;
				if (valA > valB) return currentSort === 'asc' ? 1 : -1;
				return 0;
			})
		);
	};

	const columns = useMemo(
		() => getListTableColumns(handleSort, sortDirection),
		[sortDirection]
	);

	const handleCellClicked = (cellParams) => {
		if (cellParams.colDef?.checkboxSelection && cellParams.event?.target?.type === 'checkbox') return;
		const suburl = localStorage.getItem('suburl') || 'inventory';
		const listId = cellParams.data?.id;
		if (listId) {
			navigate(`/user/${suburl}/list-details/${listId}`);
		}
	};

	return (
		<div className="product-lists-page-wrapper d-flex flex-column h-100 overflow-auto" data-testid="product-list-page">
			{/* Top Header Bar */}
			<div className="product-pg-header">
				<div className="d-flex justify-content-start align-items-center gap-4">
					<div className="dropdown text-end">
						<a
							href="#"
							className="d-block link-dark text-decoration-none dropdown-toggle"
							id="dropdownUser1"
							data-bs-toggle="dropdown"
							aria-expanded="false"
							data-testid="product-list-filter-button"
						>
							<p data-testid="product-list-page-title">{headerDropDown}</p>
						</a>
						<ul className="dropdown-menu header-dropdown-menu font-14" aria-labelledby="dropdownUser1">
							{['All Lists', 'Active Lists', 'Inactive Lists'].map((item) => (
								<li key={item}>
									<a
										className="dropdown-item"
										onClick={() => setHeaderDropDown(item)}
										data-testid={`product-list-status-filter-${item.toLowerCase().replace(/\s+/g, '-')}`}
									>
										{item}
									</a>
								</li>
							))}
						</ul>
					</div>
				</div>

				<div className="d-flex align-items-center gap-3">
					{selectedRows?.length > 0 && (
						<div className="d-flex align-items-center gap-2" data-testid="product-list-bulk-action-menu">
							<Button
								type="button"
								button="Delete"
								className="btn-gray-header mt-0 px-3"
								onClick={handleDeleteSelected}
								dataTestId="product-list-bulk-delete"
							/>
							<Button
								type="button"
								button="Export"
								className="btn-gray-header mt-0 px-3"
								dataTestId="product-list-bulk-export"
							/>
						</div>
					)}
					<Button
						add
						type="button"
						button="Create New List"
						className="btn-primary mt-0 px-4"
						onClick={() => setIsOpenCreateModal(true)}
						dataTestId="product-list-create-button"
					/>
					<div className="dropdown text-end ms-2">
						<a
							href="#"
							className="d-block link-dark text-decoration-none"
							id="dropdownUser1"
							data-bs-toggle="dropdown"
							aria-expanded="false"
							data-testid="product-list-sort-button"
						>
							<img src={sortbydot} alt="arrow" />
						</a>
						<ul className="dropdown-menu header-dropdown-menu font-14" aria-labelledby="dropdownUser1">
							<li><span className="dropdown-item sortBy text-muted fw-bold">Sort By</span></li>
							<li><a className="dropdown-item cursor-pointer" onClick={() => handleSort('CREATED DATE')} data-testid="product-list-sort-column-created-date">Created Date</a></li>
							<li><a className="dropdown-item cursor-pointer" onClick={() => handleSort('LIST NAME')} data-testid="product-list-sort-column-list-name">List Name</a></li>
							<li><a className="dropdown-item cursor-pointer" onClick={() => handleSort('PRODUCT COUNT')} data-testid="product-list-sort-column-product-count">Product Count</a></li>
							<hr className="my-1" />
							<li><a className="dropdown-item text-muted">Import Lists</a></li>
							<li><a className="dropdown-item text-muted">Export Lists</a></li>
							<hr className="my-1" />
							<li><a className="dropdown-item text-muted">Preferences</a></li>
							<li><a className="dropdown-item cursor-pointer" onClick={handleRefresh}>Refresh</a></li>
						</ul>
					</div>
				</div>
			</div>

			{/* Main Content Area */}
			{loading ? (
				<div className="d-flex justify-content-center align-items-center flex-grow-1" data-testid="product-list-loading-state">
					<span className="Page_loader" role="status" aria-hidden="true"></span>
				</div>
			) : row.length > 0 ? (
				<>
					<div className="productTable-body flex-grow-1" data-testid="product-list-table">
						<Table
							row={row}
							coloum={columns}
							className="productTable product-lists-table"
							action={setSelectedRows}
							onCellClicked={handleCellClicked}
						/>
					</div>
					<Pagination
						currentPage={params.page}
						totalPages={totalPages}
						onChangePage={(newPage) => setParams((prev) => ({ ...prev, page: newPage }))}
						pageSize={params.pageSize}
						setParam={(value) => setParams((prev) => ({ ...prev, pageSize: value, page: 1 }))}
					/>
				</>
			) : (
				<div data-testid="product-list-empty-state">
					<DataNotFound
						title={'Organize and manage your products!'}
						subtitle={'Manage all your product groups easily.'}
						descriptionTitle={'Keep your inventory organized!'}
						descriptionSubTitle={'Manage product details, pricing, stock and more — all in one place.'}
						buttonText={'Add New List'}
						onClick={() => setIsOpenCreateModal(true)}
					/>
				</div>
			)}

			{/* Create New List Modal */}
			{isOpenCreateModal && (
				<CreateNewListModal
					isOpen={isOpenCreateModal}
					onClose={() => setIsOpenCreateModal(false)}
					onSaveSuccess={handleSaveSuccess}
				/>
			)}
		</div>
	);
};

export default ProductLists;
